import FlexisNameLogo from '@/components/FlexisLogo/FlexisNameLogo.tsx'
import { Box, Button, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageSelector } from '@/components/LanguageSelector/LanguageSelector.tsx'

export interface ErrorPageProps {
  title: string
  subtitle: string
  errorCode: number
  children: ReactNode
}

const SubTitleTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey['700'],
  textAlign: 'center',
  maxWidth: 520,
  px: 2
}))

export function ErrorPage({ title, subtitle, errorCode, children }: ErrorPageProps) {
  const { t } = useTranslation('_routes')
  const navigate = useNavigate()

  const handleBackToHome = () => {
    navigate({ to: '/' })
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: 'grey.50'
      }}
    >
      {/* Logo at top left */}
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          top: 24,
          left: 32,
          right: 32,
          zIndex: 1
        }}
      >
        <FlexisNameLogo />

        <LanguageSelector />
      </Box>

      {/* Large 403 background text */}
      <Typography
        color="white"
        sx={{
          position: 'absolute',
          fontSize: { xs: '260px', sm: '370px', md: '500px' },
          fontWeight: 700,
          userSelect: 'none',
          zIndex: 0,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '14px',
          textShadow: '0 0 25px rgba(0, 0, 0, 0.13)',
          background:
            'linear-gradient(166deg, rgba(255, 255, 255, 0.80) 0.43%, rgba(255, 255, 255, 0.00) 101.07%)',
          backgroundClip: 'text',
          backdropFilter: 'blur(20px)'
        }}
      >
        {errorCode}
      </Typography>

      {/* Main content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          px: 2,
          maxWidth: 700
        }}
      >
        {/*Error page image*/}
        {children}

        {/* Title */}
        <Typography
          variant="h1"
          color="primary"
          sx={{
            textAlign: 'center'
          }}
        >
          {title}
        </Typography>

        {/* Subtitle */}
        <SubTitleTypography>{subtitle}</SubTitleTypography>

        {/* Back to Home button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackToHome}
          sx={{ px: 2 }}
        >
          {t('components.ErrorPages.backHome')}
        </Button>
      </Box>
    </Box>
  )
}
